import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import { CustomInput } from '../../../../components/CustomInput';
import { UploadFiles } from '../../../../components/UploadFiles';
import { CustomInputTextArea } from '../../../../components/CustomInputTextArea';
import { CustomButton } from '../../../../components/CustomButton';
import { ROUTE_FOR_ASSETS, ROUTES } from '../../../../constants/routes';
import { validationSchema } from './utils';
import { convertToRaw, EditorState, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { getPackage, patchPackage, uploadPackageImage } from '../../../../store/bonuses/actions';
import { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { PromotionDescriptionEditor } from '../../components/PromotionDescriptionEditor';
import { PACKAGE_NAME_MAX_LENGTH } from '../../constants';

export const EditPackage = () => {
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { package: pkg, fetching, error } = useSelector((state) => state.bonuses);

  const pkgData = pkg?.data?.data;

  useEffect(() => {
    dispatch(getPackage(id));
    // eslint-disable-next-line
  }, []);

  const initialValues = {
    code: pkgData?.code,
    promotion_type: 'partner_promotion',
    name: pkgData?.name,
    description: {
      en: EditorState.createWithContent(
        ContentState.createFromBlockArray(htmlToDraft(pkgData?.description?.en ?? '').contentBlocks)
      ),
      ru: EditorState.createWithContent(
        ContentState.createFromBlockArray(htmlToDraft(pkgData?.description?.ru ?? '').contentBlocks)
      ),
      kz: EditorState.createWithContent(
        ContentState.createFromBlockArray(htmlToDraft(pkgData?.description?.kz ?? '').contentBlocks)
      )
    },
    short_description: pkgData?.short_description,
    banner: pkgData?.banner
  };

  const handleSubmit = async (values) => {
    try {
      await dispatch(
        patchPackage(id, {
          ...values,
          description: {
            en: draftToHtml(convertToRaw(values.description.en.getCurrentContent())),
            ru: draftToHtml(convertToRaw(values.description.ru.getCurrentContent())),
            kz: draftToHtml(convertToRaw(values.description.kz.getCurrentContent()))
          }
        })
      );

      history.push(ROUTES.PARTNERS);
    } catch (e) {
      return;
    }
  };

  const getObjectValueFromString = (object, path) => {
    const pathArr = path.split('.');
    if (pathArr.length === 1) {
      return object[path];
    }

    let value = object;

    for (const path of pathArr) {
      if (!value[path]) {
        return value[path];
      }
      value = value[path];
    }

    return value;
  };

  if (fetching) {
    return (
      <Box
        sx={{
          marginTop: '150px',
          textAlign: 'center'
        }}>
        <CircularProgress data-testid="CircularProgress" />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        mb="24px"
        variant="h1"
        sx={{ color: 'text.primary' }}
        component="h1"
        data-testid="Title">
        Редактирование пакета
      </Typography>

      <Paper sx={{ padding: '24px', marginTop: '24px' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema()}
          validateOnChange
          validateOnBlur
          enableReinitialize
          onSubmit={handleSubmit}>
          {({ values, handleChange, setFieldValue, errors, touched }) => {
            const registerInput = (name) => ({
              name,
              value: getObjectValueFromString(values, name),
              error:
                getObjectValueFromString(touched, name) && getObjectValueFromString(errors, name)
            });

            const handleUploadFiles = async (file, field) => {
              const formData = new FormData();
              formData.append('uploaded_file', file);

              const data = await dispatch(uploadPackageImage(formData));

              setFieldValue(`banner.${field}`, data.path);
            };
            return (
              <Form>
                <Box sx={{ display: 'grid', gap: '34px' }}>
                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <CustomInput
                      {...registerInput('code')}
                      onChange={handleChange}
                      placeholder="Код"
                    />
                  </Box>

                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <Box style={{ display: 'flex', gap: '16px' }}>
                      <UploadFiles
                        title="Превью en"
                        name="banner.en"
                        setFieldValue={setFieldValue}
                        image
                        uploadFetch={(file) => handleUploadFiles(file, 'en')}
                      />
                      {values.banner?.en && (
                        <img
                          style={{ width: 200 }}
                          src={`${ROUTE_FOR_ASSETS}/api/v1/cms-loyalty/api/bonuses/packages/file/${values.banner.en}`}
                        />
                      )}
                    </Box>
                    <Box color="error.main" fontSize="1rem" textAlign="center">
                      {errors?.banner?.en}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <Box style={{ display: 'flex', gap: '16px' }}>
                      <UploadFiles
                        title="Превью ru"
                        name="banner.ru"
                        setFieldValue={setFieldValue}
                        image
                        uploadFetch={(file) => handleUploadFiles(file, 'ru')}
                      />
                      {values.banner?.ru && (
                        <img
                          style={{ width: 200 }}
                          src={`${ROUTE_FOR_ASSETS}/api/v1/cms-loyalty/api/bonuses/packages/file/${values.banner.ru}`}
                        />
                      )}
                    </Box>
                    <Box color="error.main" fontSize="1rem" textAlign="center">
                      {errors?.banner?.ru}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <Box style={{ display: 'flex', gap: '16px' }}>
                      <UploadFiles
                        title="Превью kz"
                        name="banner.kz"
                        setFieldValue={setFieldValue}
                        image
                        uploadFetch={(file) => handleUploadFiles(file, 'kz')}
                      />
                      {values.banner?.kz && (
                        <img
                          style={{ width: 200 }}
                          src={`${ROUTE_FOR_ASSETS}/api/v1/cms-loyalty/api/bonuses/packages/file/${values.banner.kz}`}
                        />
                      )}
                    </Box>
                    <Box color="error.main" fontSize="1rem" textAlign="center">
                      {errors?.banner?.kz}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <CustomInput
                      {...registerInput('name.en')}
                      onChange={handleChange}
                      placeholder="Наименование пакета en"
                      inputProps={{ maxLength: PACKAGE_NAME_MAX_LENGTH }}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <CustomInput
                      {...registerInput('name.ru')}
                      onChange={handleChange}
                      placeholder="Наименование пакета ru"
                      inputProps={{ maxLength: PACKAGE_NAME_MAX_LENGTH }}
                    />
                  </Box>
                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <CustomInput
                      {...registerInput('name.kz')}
                      onChange={handleChange}
                      placeholder="Наименование пакета kz"
                      inputProps={{ maxLength: PACKAGE_NAME_MAX_LENGTH }}
                    />
                  </Box>

                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <CustomInputTextArea
                      {...registerInput('short_description.en')}
                      onChange={handleChange}
                      placeholder="Краткое описание en"
                      style={{ height: 120, width: '100%' }}
                      onPaste={(e) =>
                        e.clipboardData.getData('text').length > 1000 && e.preventDefault()
                      }
                    />
                  </Box>
                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <CustomInputTextArea
                      {...registerInput('short_description.ru')}
                      onChange={handleChange}
                      placeholder="Краткое описание ru"
                      style={{ height: 120, width: '100%' }}
                      onPaste={(e) =>
                        e.clipboardData.getData('text').length > 1000 && e.preventDefault()
                      }
                    />
                  </Box>
                  <Box sx={{ display: 'grid', rowGap: '16px' }}>
                    <CustomInputTextArea
                      {...registerInput('short_description.kz')}
                      onChange={handleChange}
                      placeholder="Краткое описание kz"
                      style={{ height: 120, width: '100%' }}
                      onPaste={(e) =>
                        e.clipboardData.getData('text').length > 1000 && e.preventDefault()
                      }
                    />
                  </Box>

                  <PromotionDescriptionEditor
                    values={values.description}
                    errors={errors?.description}
                    onChange={(language, editorState) =>
                      setFieldValue(`description.${language}`, editorState)
                    }
                  />
                </Box>

                <Box color="error.main" fontSize="1rem" textAlign="center">
                  {error?.message}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '24px'
                  }}>
                  <Box>
                    <CustomButton
                      size="large"
                      color="secondary"
                      variant="contained"
                      component={RouterLink}
                      to={ROUTES.PARTNERS}>
                      Отмена
                    </CustomButton>

                    <CustomButton
                      type="submit"
                      sx={{ marginLeft: '16px', width: '146px' }}
                      size="large"
                      variant="contained">
                      Сохранить
                    </CustomButton>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
};
